import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Loader2, Users, ChevronDown, ChevronUp, Sparkles, Filter,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { CandidateReviewPanel } from "@/components/team/CandidateReviewPanel";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const STAGES = [
    { key: "all", label: "All stages" },
    { key: "screened", label: "Screened" },
    { key: "interview", label: "Interview" },
    { key: "offer", label: "Offer" },
    { key: "hired", label: "Hired" },
    { key: "passed", label: "Passed" },
];

const STAGE_STYLE = {
    screened: "bg-blue-500/15 text-blue-700 dark:text-blue-400",
    interview: "bg-amber-500/15 text-amber-700 dark:text-amber-400",
    offer: "bg-violet-500/15 text-violet-700 dark:text-violet-400",
    hired: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
    passed: "bg-muted text-muted-foreground",
};

const initials = (s = "") => s.split(/\s+/).map((x) => x[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";

const CandidateRow = ({ row }) => {
    const [open, setOpen] = useState(false);
    return (
        <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} data-testid={`team-candidate-${row.id}`} className="card-soft p-5">
            <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-brand-navy text-white text-sm font-bold flex items-center justify-center shrink-0">
                    {initials(row.candidate_name || "")}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-heading font-semibold text-foreground truncate">{row.candidate_name || "Candidate"}</h3>
                        {row.match_score != null && <Badge variant="outline" className="text-[10px] mono-score">{row.match_score}%</Badge>}
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${STAGE_STYLE[row.stage] || STAGE_STYLE.screened}`}>{row.stage}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 truncate">
                        <span className="font-semibold text-foreground">{row.job_title}</span> · {row.company}
                    </div>
                </div>
                <button
                    onClick={() => setOpen((v) => !v)}
                    data-testid={`team-candidate-expand-${row.id}`}
                    className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:border-emerald-500/60 shrink-0"
                >
                    {open ? <>Close <ChevronUp size={12} /></> : <>Review <ChevronDown size={12} /></>}
                </button>
            </div>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="mt-4 overflow-hidden"
                    >
                        <CandidateReviewPanel pipelineId={row.id} />
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

const TeamCandidates = () => {
    const [rows, setRows] = useState([]);
    const [stage, setStage] = useState("all");
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/team/dashboard");
            setRows(data.pipeline || []);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => stage === "all" ? rows : rows.filter((r) => r.stage === stage), [rows, stage]);

    if (loading) return <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>;

    return (
        <div data-testid="team-candidates-page">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                <div>
                    <p className="overline">Review room</p>
                    <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">Candidates</h1>
                    <p className="text-sm text-muted-foreground mt-1">{rows.length} candidate{rows.length === 1 ? "" : "s"} across all your team&apos;s roles.</p>
                </div>
                <div className="w-40 flex items-center gap-2">
                    <Filter size={14} className="text-muted-foreground" />
                    <Select value={stage} onValueChange={setStage}>
                        <SelectTrigger data-testid="team-stage-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>{STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
            </div>

            {filtered.length === 0 ? (
                <div className="card-soft p-12 text-center">
                    <Users size={22} className="mx-auto text-emerald-500" />
                    <h3 className="mt-3 font-heading font-semibold text-xl text-foreground">
                        {stage === "all" ? "No candidates in pipeline yet." : "No candidates at this stage."}
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">Your hiring manager will add candidates from AI Hiring Briefs as they arrive.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {filtered.map((r) => <CandidateRow key={r.id} row={r} />)}
                </div>
            )}

            <div className="mt-8 card-soft p-4 flex items-start gap-3">
                <Sparkles size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">
                    Expand any candidate to add comments and rate them 1–5 stars. Every teammate&apos;s feedback rolls into a shared average.
                </p>
            </div>
        </div>
    );
};

export default TeamCandidates;
